import React, { useMemo, useState } from 'react';
import i18next from 'i18next';
import { stakeholderCategoryOptions, stakeholderSubCategoriesOptions } from '../../../helper';
import { assessmentOptions } from '../helper';
import Year from './riskHistoryYear';
import { getCountryName } from '../../../../../helpers/map';
import { riskSubTypesOptions, riskTypesOptions } from '../../../../../components/SubjectsMap/config';

export const labelsNoTranslation = {
  5: 'Very High',
  4: 'High',
  3: 'Medium',
  2: 'Low',
  1: 'Very Low',
  0: 'None',
};

export const labels = {
  5: i18next.t('Very High'),
  4: i18next.t('High'),
  3: i18next.t('Medium'),
  2: i18next.t('Low'),
  1: i18next.t('Very Low'),
  0: i18next.t('None')
};

export const colors = {
  5: '#CF1322',
  4: '#FF4D4F',
  3: '#FA8C16',
  2: '#FADB14',
  1: '#73D13D',
  0: '#f1f1f1',
};

export default function RiskEvolution({
  events = [],
  data: defaultData = [],
  linkingData = {},
  getRiskPlanData = () => { },
  hasRisk = false,
  managementDisabbled = false,
  form = { planning: {} },
  onPlan = () => { },
  onManage = () => { },
  canEdit,
  location,
  updatedAt,
  score,
  category,
  subCategory,
  organisationName = null,
  subGroup,
  subSection,
}) {
  const [opened, setOpened] = useState([]);
  // const { planning: { inputs = { measure: { options: [] }, status: { options: [] } } } } = form;

  const toggleOpened = (index) => {
    if (opened.includes(index)) {
      setOpened((prev) => [...prev.filter((p) => p !== index)]);
    } else {
      setOpened((prev) => [...prev, index]);
    }
  };

  const data = useMemo(() => {
    const all = [];

    defaultData.forEach((d) => {
      all.push(d);
    });

    all.reverse();

    return all;
  }, [defaultData])

  const getPlan = (all) => {
    const cat = riskTypesOptions.find((c) => c.value === category);
    const type = riskSubTypesOptions.find((c) => c.value === subCategory);
    const sC = all.stakeholder ? stakeholderCategoryOptions.find((v) => v.value === all.stakeholder.category) : null;
    const sSC = all.stakeholder ? stakeholderSubCategoriesOptions.find((v) => v.value === all.stakeholder.subCategory) : null;
    const stakeholder = all.stakeholder ? {
      ...all.stakeholder,
      id: all.stakeholder.datastakeId,
      country: getCountryName(all.stakeholder.country),
      name: all.stakeholder.name,
      category: sC ? sC.label[i18next.language] : '',
      subCategory: sSC ? sSC.label[i18next.language] : '',
    } : null;

    const plan = {
      updatedAt,
      informationSource: organisationName,
      typeOfMeasure: all.type,
      objective: all.objective,
      status: all.status,
      locationType: location.locationType.value,
      area: location.locationName[i18next.language],
      riskType: type ? type.label[i18next.language] : '',
      riskCategory: cat ? cat.label[i18next.language] : '',
      score,
      date: all.proposedDate,
      details: all.details,
      stakeholder,
      title: all.title,
      events: all.events,
      completedAt: all.completedAt,
    };

    return plan;
  };

  const getEvents = (typeOfEvent, config) => {
    const { management } = config;
    const all = management.filter((m) => m.subject !== 'measure');
    const allEvents = all.map((e) => {
      if (typeof e === 'string') {
        return events.find((ev) => ev.id === e || ev.id === e.id);
      }
      return events.find((ev) => ev.id === e || ev.id === e.id);
    }).filter((e) => e);
    return allEvents.filter(ev => ev.typeOfEvent === typeOfEvent);
  }

  const getLabelsAndDesc = (score) => {
    const options = assessmentOptions;
    let impactLabel = null;
    let threatLabel = null;
    let vulnerabillityLabel = null;
    let impactDesc = null;
    let threatDesc = null;
    let vulnerabillityDesc = null;
    if (options[subSection]) {
      if (options[subSection].all) {
        if (options[subSection][subGroup]) {
          if (options[subSection][subGroup].threat) {
            const th = options[subSection][subGroup].threat;
            const thO = th.find((v) => v.value === score.threat);
            if (thO) {
              if (thO.label) {
                threatLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                threatDesc = thO.description[i18next.language];
              }
            }
          } else {
            const th = options[subSection].all.threat;
            const thO = th.find((v) => v.value === score.threat);
            if (thO) {
              if (thO.label) {
                threatLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                threatDesc = thO.description[i18next.language];
              }
            }
          }

          if (options[subSection][subGroup].impact) {
            const th = options[subSection][subGroup].impact;
            const thO = th.find((v) => v.value === score.impact);
            if (thO) {
              if (thO.label) {
                impactLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                impactDesc = thO.description[i18next.language];
              }
            }
          } else {
            const th = options[subSection].all.impact;
            const thO = th.find((v) => v.value === score.impact);
            if (thO) {
              if (thO.label) {
                impactLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                impactDesc = thO.description[i18next.language];
              }
            }
          }

          if (options[subSection][subGroup].vulnerability) {
            const th = options[subSection][subGroup].vulnerability;
            const thO = th.find((v) => v.value === score.vulnerability);
            if (thO) {
              if (thO.label) {
                vulnerabillityLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                vulnerabillityDesc = thO.description[i18next.language];
              }
            }
          } else {
            const th = options[subSection].all.vulnerability;
            const thO = th.find((v) => v.value === score.vulnerability);
            if (thO) {
              if (thO.label) {
                vulnerabillityLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                vulnerabillityDesc = thO.description[i18next.language];
              }
            }
          }
        }
      }
    }

    return {
      impact: {
        label: impactLabel,
        description: impactDesc,
      },
      threat: {
        label: threatLabel,
        description: threatDesc,
      },
      vulnerability: {
        label: vulnerabillityLabel,
        description: vulnerabillityDesc,
      },
    }
  }

  return (
    <div className="riskEvolution risk-widget">
      <div className="head">
        <h1 className="risk-header">{i18next.t("Risk History")}</h1>
      </div>
      <div className="body">
        {!data.length ? (
          <div className="risk-history-empty">
            <img src="/assets/images/empty_state_icon.svg" alt="Empty" />
            <div className="ant-empty-description">{i18next.t('Not answered')}</div>
          </div>
        ) : (
          <div className="risk-history">
            {data.map((v, i) => (
              <Year
                canEdit={canEdit}
                key={`risk-history-years--${i + 1}`}
                isCurrent={!v.completedAt}
                opened={opened.includes(i)}
                data={v}
                onToggle={() => toggleOpened(i)}
                nextYear={data[i + 1] || null}
                descriptions={getLabelsAndDesc(v.assessment)}
                riskMitigationMeasures={getRiskPlanData(v).map((m) => getPlan(m))}
                associatedEvents={{
                  incidents: getEvents('incident', v),
                  mitigationActions: getEvents('riskMitigationMeasures', v),
                  other: getEvents('other', v),
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
